// A store's walking order, learned from the order things go in the basket
// (stores.route, 037). The route is the household's item keys (normalize
// itemKey, so word order doesn't matter) from the entrance to the tills.
//
// Learning blends, it doesn't overwrite: one trip where the milk was
// forgotten and fetched last moves it halfway, not to the end. Items the trip
// didn't pass keep their place; new ones land where they were picked,
// between the known items around them.
//
// Shopping mode lists the open items in that order (`routeOrder`), one aisle
// at a time: the categories in the order the store walks them, and inside each
// the items in their own learned order. So a tomato once ticked after the milk
// stays with the produce. An item the store hasn't seen yet goes at the end of
// its aisle, and an aisle the route doesn't know at all goes after everything
// known, in the list's own category order.

import { itemKey, wordsKey } from './normalize'

/** What the route reads of a list item (an items row, on the phone or in the watch function). */
export interface RouteItem {
  name: string
  category_id: string | null
  sort_order: number
}

/** The most keys a route keeps (store_route_ok allows 1000). */
export const ROUTE_MAX = 1000

const positions = (route: string[]) => {
  const span = Math.max(route.length - 1, 1)
  return new Map(route.map((k, i) => [k, i / span]))
}

/** The route after a trip that picked `picked` (item keys, in basket order). */
export function learnRoute(route: string[], picked: string[]): string[] {
  const seq = [...new Set(picked.filter(Boolean))]
  if (seq.length === 0) return route
  const old = positions(route)
  const known = seq.flatMap(k => (old.has(k) ? [old.get(k)!] : []))
  // The stretch of the old route this trip walked; the whole store when it can't tell.
  const lo = known.length >= 2 ? Math.min(...known) : 0
  const hi = known.length >= 2 ? Math.max(...known) : 1
  const step = seq.length > 1 ? (hi - lo) / (seq.length - 1) : 0
  const next = new Map(old)
  seq.forEach((k, i) => {
    const seen = seq.length > 1 ? lo + step * i : (lo + hi) / 2
    const before = old.get(k)
    next.set(k, before === undefined ? seen : (before + seen) / 2)
  })
  const ordered = [...next]
    .map(([k, p], i) => ({ k, p, i }))
    .sort((a, b) => a.p - b.p || a.i - b.i)
    .map(e => e.k)
  if (ordered.length <= ROUTE_MAX) return ordered
  // Too long: forget keys this trip didn't pick, from the end.
  const keep = new Set(seq)
  let over = ordered.length - ROUTE_MAX
  const out: string[] = []
  for (let i = ordered.length - 1; i >= 0; i--) {
    if (over > 0 && !keep.has(ordered[i])) { over--; continue }
    out.push(ordered[i])
  }
  return out.reverse()
}

/**
 * The open items in the store's order, an aisle at a time. `memory` is the
 * household's nameKey → category (autocategory buildCategoryMemory), which
 * says where the route's own items sit; the items on the list say it too.
 *
 * An aisle's place is the median place of its items on the route, so a few
 * late ticks (forgotten and fetched, or ticked at the till) don't move the
 * whole aisle. Inside it: the items the route knows by their place, then the
 * rest A–Z in `locale`.
 */
export function routeOrder<T extends RouteItem>(
  items: T[],
  route: string[],
  categories: { id: string; sort_order: number }[],
  memory: Map<string, string> = new Map(),
  locale = 'en',
): T[] {
  const rank = positions(route)
  // Which aisle each route key is in: the list's own items first, then the history's.
  const aisleOfKey = new Map([...memory].map(([k, c]) => [wordsKey(k), c as string | null]))
  for (const item of items) aisleOfKey.set(itemKey(item.name), item.category_id)
  const placesByAisle = new Map<string | null, number[]>()
  for (const [k, p] of rank) {
    if (!aisleOfKey.has(k)) continue
    const aisle = aisleOfKey.get(k)!
    placesByAisle.set(aisle, [...(placesByAisle.get(aisle) ?? []), p])
  }
  const aisleRank = new Map([...placesByAisle].map(([aisle, ps]) => [aisle, median(ps)]))
  const catOrder = new Map(categories.map(c => [c.id, c.sort_order]))
  const placed = items.map(item => ({
    item,
    // Aisles the store knows by their place; then the rest after them, in the list's category order.
    // The uncategorised are no aisle of the store's: always last, together.
    aisle: item.category_id ? aisleRank.get(item.category_id) ?? Number.POSITIVE_INFINITY : Number.POSITIVE_INFINITY,
    cat: item.category_id ? catOrder.get(item.category_id) ?? Number.MAX_SAFE_INTEGER : Number.MAX_SAFE_INTEGER,
    own: rank.get(itemKey(item.name)) ?? Number.POSITIVE_INFINITY,
  }))
  return placed
    .sort((a, b) => a.aisle - b.aisle || a.cat - b.cat || a.own - b.own
      || a.item.name.localeCompare(b.item.name, locale, { sensitivity: 'base' }) || a.item.sort_order - b.item.sort_order)
    .map(e => e.item)
}

function median(xs: number[]): number {
  const s = [...xs].sort((a, b) => a - b)
  const mid = Math.floor(s.length / 2)
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2
}

/** The item keys of a finished basket, in the order things went in. */
export function pickedOrder(bought: { name: string; done_at: string | null }[]): string[] {
  return bought
    .filter(b => b.done_at)
    .sort((a, b) => (a.done_at ?? '').localeCompare(b.done_at ?? ''))
    .map(b => itemKey(b.name))
}

/** A bought row as the route reads it: `done_by` is who picked it (046). */
export interface PickedItem {
  id?: string
  name: string
  done_at: string | null
  done_by?: string | null
}

/**
 * A finished basket as walks: each shopper's own picks in the order they went
 * in, the first to start first. Two people shopping together tick in between
 * each other from different aisles, so the basket's merged order zig-zags
 * across the store; each one's own is a real walk. Rows with no picker (an
 * older client) are one walk between them.
 */
export function pickedOrders(bought: PickedItem[]): string[][] {
  const byPicker = new Map<string, PickedItem[]>()
  for (const b of bought) {
    if (!b.done_at) continue
    const who = b.done_by ?? ''
    const list = byPicker.get(who)
    if (list) list.push(b)
    else byPicker.set(who, [b])
  }
  return [...byPicker.values()]
    .map(rows => [...rows].sort((a, b) => (a.done_at ?? '').localeCompare(b.done_at ?? '')))
    .sort((a, b) => (a[0].done_at ?? '').localeCompare(b[0].done_at ?? ''))
    .map(rows => rows.map(r => itemKey(r.name)))
}

/**
 * The walks of a basket someone put in order by hand (trips.picked_order, item
 * ids in the order walked): that one walk, the items it doesn't name after it
 * as they went in. Without an order, pickedOrders.
 */
export function walksOf(bought: PickedItem[], order: readonly string[] | null | undefined): string[][] {
  if (!order?.length) return pickedOrders(bought)
  const at = new Map(order.map((id, i) => [id, i]))
  const rest = pickedOrder(bought.filter(b => !b.id || !at.has(b.id)))
  const ordered = bought
    .filter(b => b.id && at.has(b.id))
    .sort((a, b) => at.get(a.id!)! - at.get(b.id!)!)
    .map(b => itemKey(b.name))
  return [[...ordered, ...rest]]
}

/** The route after a trip: each walk learned in turn. */
export function learnTrip(route: string[], walks: string[][]): string[] {
  return walks.reduce(learnRoute, route)
}

/**
 * A store's route learned again from its trips, oldest first (each one's
 * walks) — after a past trip was put in order, moved to another store or
 * deleted. Learning blends, so the trips replayed in order give the route
 * the store would have had.
 *
 * The history holds only the newer trips, so `old.route` (the route as it was)
 * keeps what older ones taught: a key the replay never saw stays where it was,
 * after the old key before it. Except `old.forget`: the keys of a trip taken
 * away from this store, which only it may have taught.
 */
export function replayRoute(
  trips: { finishedAt: number; walks: string[][] }[],
  old?: { route: readonly string[]; forget?: Iterable<string> },
): string[] {
  const replayed = [...trips]
    .sort((a, b) => a.finishedAt - b.finishedAt)
    .reduce((route, t) => learnTrip(route, t.walks), [] as string[])
  if (!old) return replayed
  const forget = new Set(old.forget)
  const seen = new Set(replayed)
  const out = [...replayed]
  let after = -1 // where in `out` the last old key that is there sits
  for (const k of old.route) {
    if (seen.has(k)) {
      after = out.indexOf(k)
    } else if (!forget.has(k)) {
      out.splice(++after, 0, k)
      seen.add(k)
    }
  }
  return out.slice(0, ROUTE_MAX)
}
